import Block from '../Block';
import ToolbarContext, { ToolbarContextType } from '../ToolbarContext';

import React, {
  Component,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
} from 'react';

type ChildProps = {
  onMouseMoveCapture: MouseEventHandler;
  onMouseOutCapture: MouseEventHandler;
};

export interface ToolbarHoverProps<T extends ChildProps> {
  block: Block<any>;
  children: ((props: ChildProps) => ReactNode) | ReactElement<T>;
  delay?: number;
}

type Props<T extends ChildProps> = ToolbarHoverProps<T> & typeof defaultProps;

const defaultProps = Object.freeze({
  delay: 2500,
});

export default class ToolbarHover<T extends ChildProps> extends Component<
  Props<T>
> {
  static defaultProps = defaultProps;

  private _timer: any = null;
  private _ctx: ToolbarContextType | null = null;

  componentWillUnmount() {
    this._clearTimer();
    if (this._ctx) {
      this._ctx.mouseOut(this.props.block);
      this._ctx.blur(this.props.block);
    }
  }

  render() {
    const { children } = this.props;
    const props = {
      onMouseMoveCapture: this._handleMouseMove,
      onMouseOutCapture: this._handleMouseOut,
    };
    return (
      <ToolbarContext.Consumer>
        {context => {
          this._ctx = context;
          if (typeof children === 'function') {
            return children(props);
          }
          if (isValidElement(children)) {
            return cloneElement(children as any, props);
          }
        }}
      </ToolbarContext.Consumer>
    );
  }

  private _clearTimer() {
    if (this._timer) clearTimeout(this._timer);
  }

  private _handleMouseMove = () => {
    this._clearTimer();
    this._timer = setTimeout(this._handleMouseOut, this.props.delay);
    if (this._ctx) this._ctx.mouseOver(this.props.block);
  };

  private _handleMouseOut = () => {
    if (this._ctx) this._ctx.mouseOut(this.props.block);
  };
}
